import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProject } from "../types/Project/IProject";
import { ITeam } from "../types/ITeam";
import { IAutoCloseSetting, IAutoOpenSetting, ICallAssignment, ITicket, ITicketOptions } from "../types/Ticket/ITicket";
import { Api } from "../redux/api/BaseApi";
import { IPriority } from "../types/Priority/IPriority";
import IForm, { IField } from "../types/Form/IForm";
import { IStatus } from "../types/Ticket/Status/IStatus";
import { ISLA } from "../types/ISLA";
import { IStaff } from "../types/IStaff";
import { Features } from "../types/User/IUserProfile";
import { HierarchyProps } from "../types/Props/HierarchyProps";
import { IUser } from "../types/User/IUser";
import { ILogOptions } from "../types/User/ILogOptions";
import { ITicketAsset } from "../types/Ticket/ITicketAsset";
import { UserApi } from "../redux/api/UserApi";
import { ITemplateListItem } from "../types/Ticket/ITemplate";
import { ProjectApi } from "../redux/api/ProjectApi";
import { SplitActionOptions } from "../types/Action/IAction";
import { IWorkstream } from "../types/Gameplan/IWorkstream";
import { IWorkstreamAssignment } from "../types/Ticket/IWorkstreamAssignment";

export type TaggedUserType = {
  label: string;
  value: string;
}

const initialTicketState = {
  ID: 0,
  Call_Desc: "",
  Company_Id: 0,//9000,
  Project_Id: 0, //5009, 
  SLA_Id: 0,
  Priority_Id: 0,
  Company_Contact: 0,//3,
  Company_Address_Id: 1,
  Assigned_Team_Id: 0,
  CustomFormId: -1,
  Contact_Name: "",
  Bulk_Contact_Ids: [],
  Start_Date: null,
  End_Date: null,
  MergeActionsFromCallId: 0,
  MergeActionsIds: []
} as ITicket;

const TicketSlice = createSlice({
  name: "newTicket",
  initialState: {
    //initialAction: null as IAction,
    ticket: initialTicketState,
    features: null as Features,
    selectedForm: null as IForm,
    selectedProject: null as IProject,
    selectedPriority: null as IPriority,
    defaultTeamId: 0,
    bulkLog: false,
    logOptions: null as ILogOptions,
    // The expanded/collapsed state of the form
    formState: ['details', 'description', 'workstream-section'] as string[],
    TaggedUsers: [] as TaggedUserType[],
    mentionFocused: "",
    splitFromTicketOptions: {} as SplitActionOptions,
    lastFlaggedNoteConfirmedId: 0
  },
  reducers: {
    SetLastFlaggedNoteConfirmedId: (state, action: PayloadAction<number>) => {
      state.lastFlaggedNoteConfirmedId = action.payload;
    },
    SetSplitOptions: (state, action: PayloadAction<SplitActionOptions>) => {
      state.splitFromTicketOptions = action.payload;
      state.ticket.MergeActionsFromCallId = action.payload?.TicketId;
      state.ticket.MergeActionsIds = action.payload?.Ids;
    },
    SetBulkLog: (state, action: PayloadAction<boolean>) => {
      state.bulkLog = action.payload;
    },
    SetTemplate: (state, action: PayloadAction<ITemplateListItem>) => {
      state.ticket.Template_Id = action.payload?.Id;
    },
    SetTemplatePopulated: (state, action: PayloadAction<boolean>) => {
      state.ticket.Template_Populated = action.payload;
    },
    SetFormState: (state, action: PayloadAction<string[]>) => {
      state.formState = action.payload;
    },
    SetTicket: (state, action: PayloadAction<ITicket>) => {
      state.ticket = action.payload;
    },
    SetWorkstreamAssignment: (state, action: PayloadAction<IWorkstreamAssignment[]>) => {
      state.ticket.Workstream_Assignment = action.payload;
      console.log('after SetWorkstreamAssignment', state.ticket.Workstream_Assignment);
    },
    SetLogOptions: (state, action: PayloadAction<ITicketOptions>) => {
      state.ticket.LogOptions = action.payload;
    },
    SetMyOptions: (state, action: PayloadAction<ILogOptions>) => {
      state.logOptions = action.payload;
    },
    SetDueDate: (state, action: PayloadAction<Date>) => {
      state.ticket.Due_Dt = action.payload.toISOString();
    },
    SetForm: (state, action: PayloadAction<IForm>) => {
      //Don't do this when we load the initial ticket details;
      if (state.ticket.CustomFormId !== action.payload?.ID) {
        state.selectedForm = action.payload;
        state.ticket.CustomFormId = action.payload?.ID;
        state.ticket.CustomFormName = action.payload?.Name;
        state.ticket.CustomFormFields = action.payload?.Fields;

        //Set the default field values if they haven't been set
        state.ticket.CustomFormFields = state.ticket.CustomFormFields?.map(ff => {
          if (!ff.VALUES || ff.VALUES.length === 0) {
            let newValues = ff.ConfigSettings.Default?.split(',')// || ff.ConfigSettings.DefaultValue;

            //Date field
            if (ff.Field_Type_Id === 3 || ff.Field_Type_Id === 5 || ff.Field_Type_Id === 6 || ff.Field_Type_Id === 7 || ff.Field_Type_Id === 8 || ff.Field_Type_Id === 9) {

              if (ff.ConfigSettings.DefaultValue?.toString().length > 0) {
                let defVal = ff.ConfigSettings.DefaultValue;
                //Check for null date
                if (ff.Field_Type_Id === 3)
                  defVal = defVal.replace('0001-01-01T00:00:00', '')

                if (isNaN(defVal))
                  newValues = defVal?.toString().split(',').filter(f => f !== '')
                else //Numeric or date
                  newValues = [defVal]
              }

            }
            return ({
              ...ff, VALUES: newValues//?.filter(v=>v!==undefined)
            });
          }
          return ff;
        })
      }
    },
    SetField: (state, action: PayloadAction<IField>) => {
      //state.ticket.CustomFormFields?.map(f => f.UID === action.payload.UID ? {...f, VALUES:action.payload.VALUES} : f);
      let foundField = state.ticket.CustomFormFields?.filter(f => f.UID === action.payload.UID)
      if (foundField.length > 0) {
        foundField[0].VALUES = action.payload.VALUES;
        foundField[0].ConfigSettings = action.payload.ConfigSettings;
      }
      state.ticket.Last_Update_Dt = new Date().toISOString();
    },
    SetClient: (state, action: PayloadAction<number>) => {
      state.ticket.Company_Id = action.payload;
    },
    SetType: (state, action) => {
      state.ticket.Type_Id = action.payload;
    },
    SetStatus: (state, action: PayloadAction<IStatus>) => {
      state.ticket.Call_Status = action.payload?.Call_Status
      state.ticket.Call_Status_Id = action.payload?.ID;
      state.ticket.Open_Status = action.payload?.Open_status;
    },
    SetAssigned: (state, action: PayloadAction<IStaff[]>) => {
      state.ticket.Call_Assignment = action.payload?.map<ICallAssignment>(m => {
        return {
          Contact_Name: m.F_Name ? m.F_Name + ' ' + m.L_Name : m.Full_Name,
          Contact_Id: m.ID
        } as ICallAssignment
      });
    },
    SetAutoCloseSetting: (state, action: PayloadAction<IAutoCloseSetting>) => {
      state.ticket.AutoCloseSettings = action.payload;
    },
    SetAutoOpenSetting: (state, action: PayloadAction<IAutoOpenSetting>) => {
      state.ticket.AutoOpenSettings = action.payload;
    },
    SetBulkContacts: (state, action: PayloadAction<IStaff[]>) => {
      state.ticket.Bulk_Contact_Ids = action.payload?.map(m => m.ID);
    },
    SetAssociatedContacts: (state, action: PayloadAction<IStaff[]>) => {
      state.ticket.Associated_Contact_Ids = action.payload?.map(m => m.ID);
    },
    SetProject: (state, action: PayloadAction<IProject>) => {
      state.selectedProject = action.payload;
      state.ticket.Project_Id = action.payload?.ID | 0;
      //only when SLAs are on
      if (state.features.SLAs)
        state.ticket.SLA_Id = action.payload?.Default_SLA_Id || 0;

      state.ticket.Priority_Set_Id = action.payload?.Priority_Set_Id;
      state.ticket.Priority_Id = action.payload?.Default_Priority_Id || 0;
      state.ticket.Is_Workstream = action.payload?.Workstream_Enabled ? true : false;
    },
    SetWorkstream: (state, action: PayloadAction<IWorkstream>) => {
      //console.log('SetWorkstream', action.payload)
      state.ticket.WorkstreamId = action.payload?.Workstream_Id;
      state.ticket.WorkstreamName = action.payload?.Workstream_Name;
      //Default start and end date
      //state.ticket.Start_Date = action.payload?.StartDt;
      //state.ticket.End_Date = action.payload?.EndDt;
    },
    SetEffort: (state, action: PayloadAction<number>) => {
      state.ticket.Effort_Mins = action.payload;
    },
    SetStartDate: (state, action: PayloadAction<Date>) => {
      state.ticket.Start_Date = action.payload;
    },
    SetEndDate: (state, action: PayloadAction<Date>) => {
      state.ticket.End_Date = action.payload;
    },
    SetSLA: (state, action: PayloadAction<ISLA>) => {
      //only when SLAs are on
      if (state.features.SLAs)
        state.ticket.SLA_Id = action.payload.SLA_ID === undefined ? 0 : action.payload.SLA_ID;
    },
    SetPriority: (state, action: PayloadAction<IPriority>) => {
      state.selectedPriority = action.payload;
      state.ticket.Priority_Id = action.payload?.ID;
    },
    SetTeam: (state, action: PayloadAction<ITeam>) => {
      state.ticket.Assigned_Team_Id = action.payload.ID;
    },
    SetAssets: (state, action: PayloadAction<ITicketAsset[]>) => {
      state.ticket.Assets = action.payload;
    },
    SetLinkedKbs: (state, action: PayloadAction<number[]>) => {
      state.ticket.Linked_Kb_Ids = action.payload;
    },
    SetEndUserName: (state, action: PayloadAction<string>) => {
      state.ticket.Contact_Name = action.payload;
    },
    SetEndUser: (state, action: PayloadAction<IUser>) => {
      console.log('SetEndUser', action.payload)
      //state.ticket.Contact_Name = action.payload?.FullName
      state.ticket.Company_Id = action.payload?.Company_Id | 0;
      state.ticket.Company_Name = action.payload?.Company_Name;
      state.ticket.Company_Type_Id = action.payload?.Company_Type_Id;
      state.ticket.Company_Address_Id = action.payload?.Company_Address_Id | 0;
      state.ticket.Address_Name = action.payload?.Address_Name;
      state.ticket.Company_Contact = action.payload?.ID;
      //state.ticket.Company_Contact_Uid = action.payload?.UId;
    },
    SetEndUserUid: (state, action: PayloadAction<string>) => {
      console.log('Ticket slice - SetEndUserUid')
      state.ticket.Company_Contact_Uid = action.payload || null;
    },
    SetDescription: (state, action) => {
      state.ticket.Call_Desc = action.payload;
    },
    SetTaggedDescription: (state, action) => {
      state.ticket.TaggedDescription = action.payload;
    },
    SetMentionFocused: (state, action) => {
      state.mentionFocused = action.payload
    },
    SetTaggedUsers: (state, action) => {
      state.TaggedUsers = action.payload;
    },
    ResetTicket: (state, action) => {
      state.ticket = { ...initialTicketState, Assigned_Team_Id: state.defaultTeamId, LogOptions: state.ticket?.LogOptions };
      state.selectedProject = null;
      state.splitFromTicketOptions = {} as SplitActionOptions;
      //console.log('Ticket reset!')
    },
    SetHierarchy: (state, action: PayloadAction<HierarchyProps>) => {
      if (action.payload.IsParent) {
        state.ticket.Is_Parent = true;
        state.ticket.Parent_Call_Id = null;
      }
      else if (!isNaN(action.payload.Parent_Ticket_Id)) {
        state.ticket.Is_Parent = false;
        state.ticket.Parent_Call_Id = action.payload.Parent_Ticket_Id;
      }
      else {
        state.ticket.Is_Parent = false;
        state.ticket.Parent_Call_Id = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      //The new ticket should initially be assigned to the current user
      Api.endpoints.getUserProfile.matchFulfilled, (state, { payload }) => {
        //Default log a ticket team.
        if (payload.Features.Your_Team_Log_A_Ticket) {
          state.ticket.Assigned_Team_Id = payload.Team_Id;
          state.defaultTeamId = payload.Team_Id;
        }
        state.features = payload.Features;

        //Set the current user as assigned to the ticket
        if (!state.ticket.Call_Assignment)
          state.ticket.Call_Assignment = [
            {
              Contact_Name: payload.F_Name + ' ' + payload.L_Name,
              Contact_Id: payload.Id
            } as ICallAssignment];

        //state.initialAction = { ...state.initialAction, Staff_Id: payload.Id, Billable_Time: payload.Default_Time, Action_Type_Id: (payload.Features.Private_Actions && payload.Features.Private_Notes_Always_On) ? 7 : undefined }
      }
    );
    builder.addMatcher(
      //Sets the default project when a Company is selcted
      ProjectApi.endpoints.getProjectList.matchFulfilled,
      (state, { payload }) => {
        const prim = payload.filter(proj => { return proj.Primary_Project });
        if (prim?.length > 0 && state.ticket.ID === 0) {
          state.selectedProject = prim[0];
          state.ticket.Project_Id = prim[0].ID;
          if (state.features.SLAs)
            state.ticket.SLA_Id = prim[0].Default_SLA_Id || 0;
          state.ticket.Priority_Set_Id = prim[0].Priority_Set_Id;
          state.ticket.Priority_Id = prim[0].Default_Priority_Id || 0;
          state.ticket.Is_Workstream = prim[0].Workstream_Enabled ? true : false;
        }
      });
    builder.addMatcher(
      //The user's default state for timeline and initial action
      UserApi.endpoints.getLogOptions.matchFulfilled, (state, { payload }) => {
        payload.Timeline_Open && state.formState.push('timeline');
        payload.Action_Open && state.formState.push('action');
      });
    builder.addMatcher(
      //The system's default state for form collapsed
      ProjectApi.endpoints.getFormConfig.matchFulfilled, (state, { payload }) => {
        !payload.Log_Ticket_Collapsed && state.formState.push('form');
      });
  }
})

export const { SetClient, SetProject, SetSLA, SetPriority, SetTeam, SetEndUser, SetDescription, SetTaggedUsers, SetTaggedDescription, SetMentionFocused, ResetTicket, SetTicket, SetForm, SetField, SetType,
  SetStatus, SetAssigned, SetAssociatedContacts, SetHierarchy, SetEndUserUid, SetEndUserName, SetDueDate, SetMyOptions, SetLogOptions, SetAssets, SetStartDate, SetEndDate,
  SetFormState, SetTemplate, SetTemplatePopulated, SetBulkLog, SetBulkContacts, SetLinkedKbs, SetAutoCloseSetting, SetAutoOpenSetting, SetSplitOptions, SetWorkstream, SetEffort, SetWorkstreamAssignment,
  SetLastFlaggedNoteConfirmedId
} = TicketSlice.actions
export default TicketSlice.reducer
